import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, ShieldCheck } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
interface Section {
  title: string;
  body: React.ReactNode;
}
const sections: Section[] = [{
  title: '1. Responsable de los datos',
  body: <p data-id="element-191">
        El responsable del tratamiento de los datos personales recopilados a
        través de este sitio es{' '}
        <strong className="text-brand-charcoal" data-id="element-192">El Código del Bienestar</strong>
        , titular de la guía digital <em data-id="element-193">Resetea tu Cortisol en 21 Días</em>.
      </p>
}, {
  title: '2. Datos que se recopilan',
  body: <p data-id="element-194">
        Al realizar una compra recopilamos únicamente los datos necesarios para
        procesar tu pedido y entregarte el producto digital:{' '}
        <strong className="text-brand-charcoal" data-id="element-195">nombre</strong>,{' '}
        <strong className="text-brand-charcoal" data-id="element-196">correo electrónico</strong> y{' '}
        <strong className="text-brand-charcoal" data-id="element-197">datos de pago</strong> al
        momento de la transacción.
      </p>
}, {
  title: '3. Finalidad del tratamiento',
  body: <p data-id="element-198">
        Los datos se utilizan exclusivamente para la entrega del producto
        digital adquirido y para enviarte comunicaciones relacionadas con tu
        compra, como el enlace de descarga, soporte y mejoras de la guía.
      </p>
}, {
  title: '4. Almacenamiento y terceros',
  body: <p data-id="element-199">
        Tus datos se guardan de forma segura mediante medidas técnicas y
        organizativas razonables.{' '}
        <strong className="text-brand-charcoal" data-id="element-200">
          No compartimos tu información con terceros
        </strong>
        , salvo con los procesadores de pago necesarios para completar la
        transacción de manera segura.
      </p>
}, {
  title: '5. Derechos del usuario',
  body: <>
        <p className="mb-4" data-id="element-201">Como usuario tienes derecho a:</p>
        <ul className="list-disc pl-6 space-y-2 text-gray-700" data-id="element-202">
          <li data-id="element-203">Acceder a los datos personales que tenemos sobre ti.</li>
          <li data-id="element-204">Rectificar cualquier información inexacta o desactualizada.</li>
          <li data-id="element-205">
            Solicitar la eliminación de tus datos cuando lo consideres oportuno.
          </li>
        </ul>
      </>
}, {
  title: '6. Contacto para ejercer tus derechos',
  body: <p data-id="element-206">
        Para ejercer cualquiera de tus derechos puedes ponerte en contacto con
        nosotros a través de la página oficial de{' '}
        <strong className="text-brand-charcoal" data-id="element-207">El Código del Bienestar</strong>
        . Atenderemos tu solicitud en el menor tiempo posible.
      </p>
}, {
  title: '7. Política de cookies',
  body: <p data-id="element-208">
        Este sitio utiliza únicamente{' '}
        <strong className="text-brand-charcoal" data-id="element-209">
          cookies técnicas necesarias
        </strong>{' '}
        para el correcto funcionamiento de la página. No empleamos cookies
        publicitarias ni de seguimiento de terceros.
      </p>
}, {
  title: '8. Vigencia',
  body: <p data-id="element-210">
        La presente política de privacidad es{' '}
        <strong className="text-brand-charcoal" data-id="element-211">efectiva desde 2026</strong> y
        podrá ser actualizada en cualquier momento. Te recomendamos revisarla
        periódicamente.
      </p>
}];
export const PrivacyPolicy: React.FC = () => {
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'auto'
    });
  }, []);
  return <div className="min-h-screen bg-brand-offwhite font-sans text-brand-charcoal selection:bg-brand-mint selection:text-brand-charcoal flex flex-col" data-id="element-212">
      <Navbar data-id="element-213" />

      <main className="flex-1" data-id="element-214">
        {/* Hero / Header */}
        <section className="bg-brand-lightmint py-16 lg:py-20" data-id="element-215">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-216">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5
          }} data-id="element-217">
              <Link to="/" className="inline-flex items-center gap-2 text-sm font-medium text-brand-emerald hover:text-brand-emeraldHover transition-colors mb-6" data-id="element-218">
                <ArrowLeft className="w-4 h-4" data-id="element-219" />
                Volver al inicio
              </Link>

              <div className="flex items-center gap-3 mb-4" data-id="element-220">
                <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center shadow-sm" data-id="element-221">
                  <ShieldCheck className="w-6 h-6 text-brand-emerald" data-id="element-222" />
                </div>
                <span className="text-sm font-medium uppercase tracking-wider text-brand-emerald" data-id="element-223">
                  Documento legal
                </span>
              </div>

              <h1 className="text-4xl md:text-5xl font-serif font-bold text-brand-charcoal leading-tight mb-4" data-id="element-224">
                Política de Privacidad
              </h1>
              <p className="text-lg text-gray-600" data-id="element-225">
                Cómo recopilamos, usamos y protegemos tus datos personales en El
                Código del Bienestar.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Content */}
        <section className="py-16 lg:py-20" data-id="element-226">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-227">
            <div className="space-y-10" data-id="element-228">
              {sections.map((section, index) => <motion.article key={section.title} className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100" initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.4,
              delay: index * 0.05
            }} data-id="element-229">
                  <h2 className="text-xl md:text-2xl font-serif font-bold text-brand-emerald mb-4" data-id="element-230">
                    {section.title}
                  </h2>
                  <div className="text-gray-700 leading-relaxed text-base" data-id="element-231">
                    {section.body}
                  </div>
                </motion.article>)}
            </div>

            <div className="mt-12 text-center" data-id="element-232">
              <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 text-sm font-medium text-white bg-brand-emerald hover:bg-brand-emeraldHover rounded-full shadow-sm hover:shadow-md transition-all" data-id="element-233">
                <ArrowLeft className="w-4 h-4" data-id="element-234" />
                Volver al inicio
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer data-id="element-235" />
    </div>;
};